/**
 * aiContext — shared builders for the `applicationsContext` snapshot pushed to
 * the BE `applications_assistant` profile.
 *
 * Two callsites:
 * 1. `useApplicationsPageData.sendChat`    → full pipeline view (`/applications`)
 * 2. `usePositionDetail.sendFloatingChat`  → single-position view
 *    (`/job-positions/:id`) — passes the position metadata as `currentPosition`
 *    so the AI can also review/improve the JD on demand.
 *
 * Sprint ai_assistant_scope_fix (26/05/26).
 */
import type {
  Application,
  JobPosition,
  AiApplicationsContext,
  AiApplicationEntry,
} from '@/types';

export interface ActiveFilters {
  status?: string;
  positionId?: string;
  teamId?: string;
  search?: string;
}

/**
 * Builds the AiApplicationsContext snapshot from data already in FE memory.
 * Zero extra API calls. Capped at 50 candidates to keep prompt token cost
 * predictable — the AI can always call `getApplicationDetails` for deep dives.
 */
export function buildApplicationsContext(
  companyId: string,
  applications: Application[],
  positions: JobPosition[],
  filters: ActiveFilters,
  currentPosition?: AiApplicationsContext['currentPosition'],
  /**
   * Authoritative DB-side counters that the caller may know from outside
   * the `positions` array (e.g. when scoped to a single position the
   * `positions` argument carries only 1 entry but the company has more).
   */
  dbCounters?: { positionsTotalInDB?: number },
): AiApplicationsContext {
  const byState: Record<string, number> = {};
  for (const app of applications) {
    byState[app.state] = (byState[app.state] ?? 0) + 1;
  }

  const appsWithScore = applications.filter((a) => a.aiScore != null);
  const scores = appsWithScore.map((a) => a.aiScore as number);
  const avgScore =
    scores.length > 0
      ? Math.round(scores.reduce((sum, s) => sum + s, 0) / scores.length)
      : null;

  // Sort by score desc; unscored at the end. Cap at PROMPT_CAP (token budget).
  // When applications.length > PROMPT_CAP, the BE prompt instructs the model
  // to call the `listAllApplications` tool for full coverage — see
  // aiRuntime.buildApplicationsAssistantPrompt and sprint
  // ai_chat_ids_hide_and_db_complete (26/05/26).
  const PROMPT_CAP = 50;
  const candidates: AiApplicationEntry[] = [...applications]
    .sort((a, b) => (b.aiScore ?? -1) - (a.aiScore ?? -1))
    .slice(0, PROMPT_CAP)
    .map((a) => ({
      applicationId: a.id,
      positionId: a.positionId,
      name: `${a.candidateFirstName} ${a.candidateLastName}`,
      positionTitle: a.positionTitle,
      state: a.state,
      score: a.aiScore ?? null,
    }));

  const positionIdsInView = new Set(applications.map((a) => a.positionId));
  const positionSummaries = positions
    .filter((p) => positionIdsInView.has(p.id))
    .map((p) => {
      const posApps = applications.filter((a) => a.positionId === p.id);
      const posScores = posApps
        .map((a) => a.aiScore)
        .filter((s): s is number => s != null);
      const posAvgScore =
        posScores.length > 0
          ? Math.round(posScores.reduce((sum, s) => sum + s, 0) / posScores.length)
          : null;
      const awaitingDecision = posApps.filter((a) => a.state === 'AWAITING_DECISION').length;
      return {
        id: p.id,
        title: p.title,
        applicationCount: posApps.length,
        avgScore: posAvgScore,
        awaitingDecision,
      };
    });

  return {
    companyId,
    totalApplications: applications.length,
    byState,
    candidates,
    scoreDistribution: {
      average: avgScore,
      scored: appsWithScore.length,
    },
    positions: positionSummaries,
    activeFilters: {
      ...(filters.status && filters.status !== 'all' ? { status: filters.status } : {}),
      ...(filters.positionId && filters.positionId !== 'all' ? { positionId: filters.positionId } : {}),
      ...(filters.teamId && filters.teamId !== 'all' ? { teamId: filters.teamId } : {}),
      ...(filters.search ? { search: filters.search } : {}),
    },
    // Sprint ai_chat_ids_hide_and_db_complete (26/05/26): signal to the BE
    // prompt how complete the CANDIDATES LIST is. When the FE list is
    // larger than PROMPT_CAP, the model is instructed to call
    // listAllApplications for full coverage. `applications` is already the
    // full DB set both from useApplicationsPageData (listAllByCompany) and
    // usePositionDetail (listByPosition returns everything).
    candidatesIncludedInPrompt: candidates.length,
    candidatesTotalInDB: applications.length,
    // Positions completeness signal — single-position scopes
    // (PositionDetailPage) carry the company-wide total via dbCounters
    // so the model can answer "how many positions?" accurately.
    positionsIncludedInPrompt: positionSummaries.length,
    positionsTotalInDB: dbCounters?.positionsTotalInDB ?? positionSummaries.length,
    ...(currentPosition ? { currentPosition } : {}),
  };
}

/**
 * Builds a `currentPosition` payload from a full JobPosition entity.
 * Used by PositionDetailPage to pass the focus position to the AI.
 * Description is intentionally NOT truncated here — the BE caps it at ~2000
 * chars when building the prompt.
 */
export function buildCurrentPositionContext(
  position: JobPosition,
): NonNullable<AiApplicationsContext['currentPosition']> {
  return {
    id: position.id,
    title: position.title,
    ...(position.description ? { description: position.description } : {}),
    ...(position.seniority ? { seniority: position.seniority } : {}),
    ...(position.contractType ? { contractType: position.contractType } : {}),
    ...(position.workMode ? { workMode: position.workMode } : {}),
    ...(position.location ? { location: position.location } : {}),
    ...(position.salaryMin != null ? { salaryMin: position.salaryMin } : {}),
    ...(position.salaryMax != null ? { salaryMax: position.salaryMax } : {}),
    ...(position.salaryCurrency ? { salaryCurrency: position.salaryCurrency } : {}),
    ...(position.status ? { status: position.status } : {}),
  };
}
